import { useEffect, useRef } from 'preact/hooks';
import { ButtonSecondary } from './ButtonSecondary';

interface InstallPromptProps {
  onDismiss: () => void;
}

export function InstallPrompt({ onDismiss }: InstallPromptProps) {
  const sheetRef = useRef<HTMLDivElement>(null);

  // Focus the "Not now" button (first focusable element) on mount.
  useEffect(() => {
    if (!sheetRef.current) return;
    const focusable = sheetRef.current.querySelector<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    (focusable ?? sheetRef.current).focus();
  }, []);

  // Escape dismisses. Tab is trapped inside the sheet.
  useEffect(() => {
    const sheet = sheetRef.current;
    if (!sheet) return;

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { onDismiss(); return; }
      if (e.key !== 'Tab') return;

      const focusableEls = Array.from(
        sheet.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      );
      if (focusableEls.length === 0) return;

      const first = focusableEls[0];
      const last = focusableEls[focusableEls.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };

    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onDismiss]);

  return (
    <div
      class="fixed inset-0 z-50 flex items-end"
      role="dialog"
      aria-modal="true"
      aria-labelledby="install-prompt-title"
    >
      {/* Backdrop */}
      <div
        class="absolute inset-0 bg-black/40"
        onClick={onDismiss}
        aria-hidden="true"
      />

      {/* Bottom sheet */}
      <div
        ref={sheetRef}
        tabIndex={-1}
        class="relative w-full bg-cream rounded-t-2xl p-6 space-y-4 shadow-xl max-w-lg mx-auto focus:outline-none"
      >
        <h2
          id="install-prompt-title"
          class="text-lg font-bold text-hudson-navy"
        >
          Add to your Home Screen
        </h2>
        <p class="text-sm text-body">
          Install HBF Tracker for instant access — works offline and opens like a native app.
        </p>
        <ol class="space-y-3 text-sm text-body" aria-label="Installation steps">
          <li class="flex items-start gap-3">
            <span
              class="flex-shrink-0 w-6 h-6 rounded-full bg-hudson-navy text-white text-xs font-bold flex items-center justify-center"
              aria-hidden="true"
            >1</span>
            <span>
              Tap the <strong>Share</strong> button{' '}
              <span role="img" aria-label="share">⬆</span>{' '}
              at the bottom of Safari
            </span>
          </li>
          <li class="flex items-start gap-3">
            <span
              class="flex-shrink-0 w-6 h-6 rounded-full bg-hudson-navy text-white text-xs font-bold flex items-center justify-center"
              aria-hidden="true"
            >2</span>
            <span>Scroll down and tap <strong>Add to Home Screen</strong></span>
          </li>
          <li class="flex items-start gap-3">
            <span
              class="flex-shrink-0 w-6 h-6 rounded-full bg-hudson-navy text-white text-xs font-bold flex items-center justify-center"
              aria-hidden="true"
            >3</span>
            <span>Tap <strong>Add</strong> in the top-right corner</span>
          </li>
        </ol>
        <ButtonSecondary onClick={onDismiss}>Not now</ButtonSecondary>
      </div>
    </div>
  );
}
